// ============================================================================
// PITCH DECK VOCABULARY — pitchSections.js
// ============================================================================
// Pure module, no React and no Supabase. The section keys, rule options and
// deck settings that the pitch types admin, the selection engine and the
// renderer all agree on. If a key changes here it changes everywhere.
//
// See docs/pitch-deck-generator-plan.md section 5.2 for what each section
// renders.
// ============================================================================

// Sections a pitch type can list, in the order the plan describes them.
//   repeatable: may appear more than once (one per theme).
//   settings:   which per-section settings the admin exposes.
export const PITCH_SECTIONS = [
  { key: 'cover',        label: 'Cover',              description: 'Hero image from the first project, deck title, client name, date, office.' },
  { key: 'intro',        label: 'Introduction',       description: 'The intro text, signed by the founders. Rewritten per client on request.' },
  { key: 'practice',     label: 'Practice',           description: 'Studio bio and mission, year founded, office count.' },
  { key: 'founders',     label: 'Leadership',         description: 'Directors, partners and associates with portraits, from the team\'s job roles. The founders\' bios sit with the directors.' },
  { key: 'theme',        label: 'Theme',              description: 'A theme statement page followed by its highlight images. One section per theme.', repeatable: true, settings: ['theme_id'] },
  { key: 'index',        label: 'Project index',      description: 'Grid of the selected projects, grouped by chapter, with headline and location.' },
  { key: 'projects',     label: 'Projects',           description: 'Chapter dividers and one spread per selected project, in chapter order.' },
  { key: 'awards',       label: 'Awards',             description: 'Awards by year, all of them or only those of the selected projects.', settings: ['scope'] },
  { key: 'publications', label: 'Publications',       description: 'Publications, all of them or only those of the selected projects.', settings: ['scope'] },
  { key: 'team',         label: 'Team',               description: 'Portraits of the current design team.' },
  { key: 'closing',      label: 'Closing',            description: 'Contact details per office.' },
];

export const SECTION_BY_KEY = Object.fromEntries(PITCH_SECTIONS.map(s => [s.key, s]));

export const SECTION_SCOPES = [
  { key: 'selected', label: 'Selected projects only' },
  { key: 'all',      label: 'All' },
];

// Estimated fee bands agreed on 2026-09-03. Amounts are EUR; the engine
// converts the inquiry fee with the display rate before comparing.
export const FEE_BANDS = [
  { key: 'small',  label: 'Up to EUR 200k',     max: 200000 },
  { key: 'medium', label: 'EUR 200k to 500k',   min: 200000, max: 500000 },
  { key: 'large',  label: 'Above EUR 500k',     min: 500000 },
];

// public.clients.client_type values.
export const CLIENT_TYPES = [
  { key: 'org',    label: 'Organisation' },
  { key: 'family', label: 'Private client or family' },
];

// A format is a set of page templates. Only screen exists for now; A4 joins
// as a second entry without any change to stored decks.
export const FORMATS = [
  { key: 'screen', label: 'Screen 16:9 (1920 by 1080)', width: 1920, height: 1080 },
];

export const QUALITY_TIERS = [
  { key: 'email',        label: 'Email (2048 px images)',              maxImageWidth: 2048 },
  { key: 'presentation', label: 'Presentation (3840 px images, link)', maxImageWidth: 3840 },
];

export const DECK_STATUSES = ['draft', 'rendered', 'sent'];

// A deck rendered under this size may go out as an attachment.
export const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;

// Hashtags the selection engine reads. Hyphenated like the rest of the
// vocabulary in app_settings.commsAssetHashtags.
export const PITCH_TAGS = { hero: 'pitch-hero', supporting: 'pitch' };

// Sections a new pitch type starts with.
export const DEFAULT_SECTIONS = [
  { id: 'cover',        key: 'cover',        enabled: true, settings: {} },
  { id: 'intro',        key: 'intro',        enabled: true, settings: {} },
  { id: 'practice',     key: 'practice',     enabled: true, settings: {} },
  { id: 'founders',     key: 'founders',     enabled: true, settings: {} },
  { id: 'index',        key: 'index',        enabled: true, settings: {} },
  { id: 'projects',     key: 'projects',     enabled: true, settings: {} },
  { id: 'awards',       key: 'awards',       enabled: true, settings: { scope: 'selected' } },
  { id: 'publications', key: 'publications', enabled: true, settings: { scope: 'selected' } },
  { id: 'team',         key: 'team',         enabled: true, settings: {} },
  { id: 'closing',      key: 'closing',      enabled: true, settings: {} },
];

export const DEFAULT_RULES = { categories: [], fee_bands: [], plot_min: null, plot_max: null, client_types: [] };

let seq = 0;
export function newId(prefix) {
  seq += 1;
  return `${prefix}-${Date.now().toString(36)}${seq.toString(36)}`;
}

export function newSection(key, settings = {}) {
  const def = SECTION_BY_KEY[key];
  const base = def && def.settings ? Object.fromEntries(def.settings.map(s => [s, s === 'scope' ? 'selected' : null])) : {};
  return { id: newId(key), key, enabled: true, settings: { ...base, ...settings } };
}

export function newChapter(category, label) {
  return { id: newId('ch'), category: category || '', label: label || '', project_order: [] };
}

// Section keys that may still be added to a list: repeatable ones always,
// the rest only while absent.
export function addableSections(sections) {
  const present = new Set((sections || []).map(s => s.key));
  return PITCH_SECTIONS.filter(s => s.repeatable || !present.has(s.key));
}

// Human label for a section entry, with its theme or scope spelt out.
export function describeSection(entry, themesById = {}) {
  const def = SECTION_BY_KEY[entry.key];
  const label = def ? def.label : entry.key;
  const settings = entry.settings || {};
  if (entry.key === 'theme') {
    const theme = settings.theme_id ? themesById[settings.theme_id] : null;
    return theme ? `Theme: ${theme.label}` : 'Theme: not chosen';
  }
  if (settings.scope) {
    const scope = SECTION_SCOPES.find(s => s.key === settings.scope);
    return scope ? `${label}: ${scope.label.toLowerCase()}` : label;
  }
  return label;
}

// Defensive readers: rows written by hand or by an older client may lack a
// field, and jsonb columns come back as whatever was stored.
export function readSections(value) {
  return Array.isArray(value) ? value.filter(s => s && typeof s === 'object' && s.key).map((s, i) => ({ id: s.id || `${s.key}-${i}`, key: s.key, enabled: s.enabled !== false, settings: s.settings && typeof s.settings === 'object' ? s.settings : {} })) : [];
}

export function readChapters(value) {
  return Array.isArray(value) ? value.filter(c => c && typeof c === 'object').map((c, i) => ({ id: c.id || `ch-${i}`, category: c.category || '', label: c.label || '', project_order: Array.isArray(c.project_order) ? c.project_order.filter(Boolean) : [] })) : [];
}

export function readRules(value) {
  const r = value && typeof value === 'object' ? value : {};
  return {
    categories:   Array.isArray(r.categories)   ? r.categories   : [],
    fee_bands:    Array.isArray(r.fee_bands)    ? r.fee_bands    : [],
    client_types: Array.isArray(r.client_types) ? r.client_types : [],
    plot_min:     r.plot_min == null || r.plot_min === '' ? null : Number(r.plot_min),
    plot_max:     r.plot_max == null || r.plot_max === '' ? null : Number(r.plot_max),
  };
}

// Which fee band an amount in EUR falls in, or null when unknown.
export function feeBandFor(eur) {
  const n = Number(eur);
  if (!Number.isFinite(n) || n <= 0) return null;
  const band = FEE_BANDS.find(b => (b.min == null || n >= b.min) && (b.max == null || n < b.max));
  return band ? band.key : null;
}

// Readers for a deck row's jsonb, tolerant of rows written by an older client.
export function readDeckChapters(value) {
  return Array.isArray(value) ? value.filter(c => c && typeof c === 'object').map((c, i) => ({
    id: c.id || `ch-${i}`,
    category: c.category || '',
    label: c.label || '',
    projects: Array.isArray(c.projects) ? c.projects.filter(p => p && p.project_code).map(p => ({
      project_code: p.project_code,
      include: p.include !== false,
      confidential: !!p.confidential,
      confidential_override: !!p.confidential_override,
      hero_file_id: p.hero_file_id || null,
      image_file_ids: Array.isArray(p.image_file_ids) ? p.image_file_ids.filter(Boolean) : [],
      headline_override: p.headline_override || null,
      text_override: p.text_override || null,
      reason: p.reason || '',
      blockers: Array.isArray(p.blockers) ? p.blockers : [],
    })) : [],
  })) : [];
}

export function readDeckSections(value) {
  const base = readSections(value);
  const flags = Array.isArray(value) ? value.map(v => (v && v.flag) || null) : [];
  return base.map((s, i) => ({ ...s, flag: flags[i] || null }));
}
